/**
 * 4Women CRO — comportamiento del editor de talla y color del carrito.
 *
 * POR QUE VIVE EN UN ASSET Y NO DENTRO DEL SNIPPET.
 *
 * Este modulo estaba inline en `snippets/fw-cart-variant.liquid`. Funcionaba
 * solo cuando la clienta cargaba la pagina con el carrito YA lleno, que casi
 * nunca pasa en una primera visita. Medido en 4women.cl el 10/09/2026:
 *
 *   carrito lleno al cargar  -> customElements.get('fw-cart-variant') = true
 *                               clic en una talla: XS -> S en el carrito. OK.
 *   carrito vacio al cargar  -> se agrega desde la ficha, el cajon se pinta por
 *                               morph, aparece <fw-cart-variant> en el DOM y
 *                               constructor.name === 'HTMLElement'. Inerte.
 *
 * La razon es la de siempre en este tema: un <script> que llega por innerHTML
 * NO se ejecuta. El snippet solo imprime su script cuando hay lineas, asi que
 * con el carrito vacio el script no estaba en el HTML servido, y la unica via
 * por la que llegaba despues era el re-render del cajon. `customElements.define`
 * nunca corria y los botones no hacian nada, sin error en consola.
 *
 * Cargado desde el layout corre una vez por pagina, siempre, y el navegador
 * promociona (upgrade) los <fw-cart-variant> que aparezcan despues por morph.
 * Mismo motivo por el que ya estaban afuera fw-packs.js y fw-abrigo-quickadd.js.
 */

import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

if (!customElements.get('fw-cart-variant')) {
  class FwCartVariant extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
    }

    #onClick = (event) => {
      const boton = event.target.closest('[data-fw-variant]');
      if (!boton || boton.disabled || boton.getAttribute('aria-pressed') === 'true') return;
      event.preventDefault();
      this.#cambiar(boton);
    };

    /**
     * Cambiar talla o color es la misma operación: sacar la línea actual y
     * poner la variante nueva. Va en UNA sola llamada a /cart/update.js para
     * que el carrito nunca quede sin la prenda si la segunda falla.
     */
    async #cambiar(boton) {
      const nuevaVariante = boton.dataset.fwVariant;
      const cantidad = parseInt(boton.dataset.fwQty || '1', 10);
      const clave = this.dataset.lineKey;
      if (!nuevaVariante || !clave) return;

      const error = this.querySelector('.fw-cv__error');
      const seccion = this.closest('.shopify-section')?.id.replace('shopify-section-', '');

      this.#ocupado(true);
      if (error) error.hidden = true;

      const cuerpo = { updates: { [clave]: 0, [nuevaVariante]: cantidad } };
      if (seccion) {
        cuerpo.sections = seccion;
        cuerpo.sections_url = window.location.pathname;
      }

      try {
        const respuesta = await fetch(`${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(cuerpo),
        });
        const datos = await respuesta.json();

        if (!respuesta.ok || datos.status) {
          throw new Error(datos.description || datos.message || 'No se pudo actualizar');
        }

        // El ícono del carrito y el resto de la página escuchan este evento.
        document.dispatchEvent(
          new CartUpdateEvent({}, 'fw-cart-variant', {
            itemCount: datos.item_count,
            source: 'fw-cart-variant',
            sections: datos.sections,
          })
        );

        if (seccion && datos.sections?.[seccion]) {
          await morphSection(seccion, datos.sections[seccion]);
        } else {
          window.location.reload();
        }
      } catch (e) {
        this.#ocupado(false);
        if (error) {
          error.textContent = 'No pudimos cambiar la opción. Inténtalo de nuevo.';
          error.hidden = false;
        }
      }
    }

    /**
     * Se recuerda qué botones ya venían apagados (talla sin stock, color que
     * no existe en esa talla). Si no, al reactivar quedarían todos clicables
     * y la clienta podría pedir una variante agotada.
     */
    #ocupado(estado) {
      this.toggleAttribute('aria-busy', estado);
      const botones = this.querySelectorAll('button[data-fw-variant]');
      if (estado) {
        botones.forEach((b) => {
          if (b.disabled) b.dataset.fwEraDisabled = '1';
          b.disabled = true;
        });
        return;
      }
      botones.forEach((b) => {
        b.disabled = b.dataset.fwEraDisabled === '1';
        delete b.dataset.fwEraDisabled;
      });
    }
  }

  customElements.define('fw-cart-variant', FwCartVariant);
}
